import { useEffect, useState, type ReactNode } from "react";
import {
  CalendarDays,
  ClipboardList,
  Loader2,
  MessageSquare,
  Users,
  UserCheck,
  type LucideIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Label } from "@project/components/ui/label";
import { formatDate } from "@project/lib/date-format";
import { getApiErrorMessage } from "@project/lib/api-error";
import { visitorService } from "@project/services/admin/visitor.service";
import { visitorInquiryService } from "@project/services/admin/visitor-inquiry.service";
import {
  REFERENCE_SOURCE_OPTIONS,
  type VisitorFamilyMember,
  type VisitorRecord,
} from "@project/types/visitor";
import type { VisitorInquiryRecord } from "@project/types/visitor-inquiry";

type SectionConfig = {
  label: string;
  icon: LucideIcon;
  subtitle: string;
};

function displayValue(value?: string | number | boolean | null) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return String(value);
}

function referenceSourceLabel(value?: string | null) {
  if (!value) {
    return "—";
  }
  const match = REFERENCE_SOURCE_OPTIONS.find(option => option.value === value);
  return match?.label ?? value;
}

function ViewField({
  label,
  value,
  full = false,
}: {
  label: string;
  value?: string | number | boolean | null;
  full?: boolean;
}) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-foreground/80 mb-1.5 block text-xs font-medium">
        {label}
      </Label>
      <div className="bg-muted/40 whitespace-pre-wrap rounded-xl border border-transparent px-3 py-2.5 text-sm text-foreground">
        {displayValue(value)}
      </div>
    </div>
  );
}

function SectionPanel({
  section,
  children,
  contentClassName = "grid gap-4 md:grid-cols-2",
}: {
  section: SectionConfig;
  children: ReactNode;
  contentClassName?: string;
}) {
  const Icon = section.icon;
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex items-start gap-3 border-b border-border pb-4">
        <div className="bg-primary/10 grid h-11 w-11 shrink-0 place-items-center rounded-xl text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-serif text-lg font-bold text-foreground sm:text-xl">
            {section.label}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
            {section.subtitle}
          </p>
        </div>
      </div>
      <div className={contentClassName}>{children}</div>
    </div>
  );
}

function FamilyMemberCard({ member }: { member: VisitorFamilyMember }) {
  return (
    <div className="bg-muted/20 space-y-3 rounded-xl border border-border p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">
        Member {member.sr_no}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <ViewField label="Name" value={member.name} />
        <ViewField
          label="Relationship"
          value={member.relationship_name ?? member.relationship}
        />
        <ViewField
          label="Date of Birth"
          value={formatDate(member.date_of_birth) || member.date_of_birth}
        />
        <ViewField label="Education" value={member.education} />
      </div>
    </div>
  );
}

const VISIT_DETAILS_SECTION: SectionConfig = {
  label: "Visit Details",
  icon: CalendarDays,
  subtitle: "Visitor identity and contact information",
};

const REASON_SECTION: SectionConfig = {
  label: "Reason for Visit",
  icon: ClipboardList,
  subtitle: "Purpose of the visit and prior visit history",
};

const REFERENCE_SECTION: SectionConfig = {
  label: "Reference",
  icon: UserCheck,
  subtitle: "How they heard about Jivandhara",
};

const FAMILY_SUMMARY_SECTION: SectionConfig = {
  label: "Family Summary",
  icon: Users,
  subtitle: "Headcount of accompanying family members",
};

const FAMILY_MEMBERS_SECTION: SectionConfig = {
  label: "Family Members",
  icon: Users,
  subtitle: "Individual family member details",
};

const FEEDBACK_SECTION: SectionConfig = {
  label: "Feedback",
  icon: MessageSquare,
  subtitle: "Comments shared by the visitor",
};

/* ─── Visitor Records detail ─── */

export type VisitorDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visitorId: number | null;
  preview?: VisitorRecord | null;
};

export function VisitorDetailDialog({
  open,
  onOpenChange,
  visitorId,
  preview,
}: VisitorDetailDialogProps) {
  const [record, setRecord] = useState<VisitorRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setRecord(null);
      setError(null);
      return;
    }
    if (!visitorId) {
      return;
    }

    let ignore = false;
    setLoading(true);
    setError(null);
    setRecord(preview ?? null);

    void visitorService
      .getVisitor(visitorId)
      .then(data => {
        if (ignore) {
          return;
        }
        if (data) {
          setRecord(data);
        } else {
          setError("Visitor details could not be loaded.");
        }
      })
      .catch(err => {
        if (!ignore) {
          setError(getApiErrorMessage(err, "Failed to load visitor details."));
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [open, visitorId, preview]);

  const family = record?.family_members ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4 text-left">
          <div className="pr-8">
            <DialogTitle className="font-serif text-xl font-bold">
              {record ? `Visitor Record #${record.id}` : "Visitor Record"}
            </DialogTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {record
                ? `${record.name} · visit ${formatDate(record.date) ?? record.date ?? "—"}`
                : "Read-only view of the visitor record"}
            </p>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading && !record ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mb-3 h-8 w-8 animate-spin" />
              Loading visitor details…
            </div>
          ) : error && !record ? (
            <div className="border-destructive/30 bg-destructive/5 rounded-xl border px-4 py-6 text-center text-sm text-destructive">
              {error}
            </div>
          ) : record ? (
            <div className="space-y-6">
              <SectionPanel section={VISIT_DETAILS_SECTION}>
                <ViewField
                  label="Visit Date"
                  value={formatDate(record.date) || record.date}
                />
                <ViewField label="Visitor Name" value={record.name} />
                <ViewField
                  label="Date of Birth"
                  value={
                    record.date_of_birth
                      ? formatDate(record.date_of_birth) || record.date_of_birth
                      : undefined
                  }
                />
                <ViewField label="Age" value={record.age} />
                <ViewField
                  label="Married Status"
                  value={
                    record.married_status !== undefined
                      ? record.married_status
                        ? "Married"
                        : "Unmarried / Single"
                      : undefined
                  }
                />
                {record.married_status && record.marriage_anniversary_date ? (
                  <ViewField
                    label="Marriage Anniversary Date"
                    value={
                      formatDate(record.marriage_anniversary_date) ||
                      record.marriage_anniversary_date
                    }
                  />
                ) : null}
                <ViewField label="Occupation" value={record.occupation} />
                <ViewField label="Mobile Number" value={record.mobile_number} />
                <ViewField
                  label="Residence Phone"
                  value={record.residence_phone}
                />
                <ViewField label="Email" value={record.email} />
                <ViewField label="Address" value={record.address} full />
                <ViewField
                  label="Business Address"
                  value={record.business_address}
                  full
                />
              </SectionPanel>

              <SectionPanel section={REASON_SECTION}>
                <ViewField
                  label="Reason for Visit"
                  value={record.reason_for_visit}
                  full
                />
                <ViewField
                  label="Visited Before"
                  value={record.has_visited_before}
                />
                {record.has_visited_before ? (
                  <ViewField
                    label="Previous Visit Details"
                    value={record.previous_visit_details}
                    full
                  />
                ) : null}
              </SectionPanel>

              <SectionPanel section={REFERENCE_SECTION}>
                <ViewField
                  label="Reference Source"
                  value={referenceSourceLabel(record.reference_source)}
                />
                <ViewField label="Referrer Name" value={record.referrer_name} />
                <ViewField
                  label="Referrer Mobile"
                  value={record.referrer_mobile}
                />
              </SectionPanel>

              <SectionPanel section={FAMILY_SUMMARY_SECTION}>
                <ViewField
                  label="Total Family Members"
                  value={record.total_family_members}
                />
                <ViewField label="Male Count" value={record.male_count} />
                <ViewField label="Female Count" value={record.female_count} />
              </SectionPanel>

              <SectionPanel
                section={{
                  ...FAMILY_MEMBERS_SECTION,
                  label: `Family Members (${family.length})`,
                }}
                contentClassName="space-y-3"
              >
                {family.length === 0 ? (
                  <p className="py-2 text-center text-sm text-muted-foreground">
                    No family members recorded.
                  </p>
                ) : (
                  family.map((member, index) => (
                    <FamilyMemberCard
                      key={member.id ?? `${member.sr_no}-${index}`}
                      member={member}
                    />
                  ))
                )}
              </SectionPanel>

              <SectionPanel section={FEEDBACK_SECTION}>
                <ViewField label="Feedback" value={record.feedback} full />
              </SectionPanel>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Visitor Inquiry detail ─── */

export type VisitorInquiryDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inquiryId: number | null;
  preview?: VisitorInquiryRecord | null;
};

export function VisitorInquiryDetailDialog({
  open,
  onOpenChange,
  inquiryId,
  preview,
}: VisitorInquiryDetailDialogProps) {
  const [record, setRecord] = useState<VisitorInquiryRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setRecord(null);
      setError(null);
      return;
    }
    if (!inquiryId) {
      return;
    }

    let ignore = false;
    setLoading(true);
    setError(null);
    setRecord(preview ?? null);

    void visitorInquiryService
      .getInquiry(inquiryId)
      .then(data => {
        if (ignore) {
          return;
        }
        if (data) {
          setRecord(data);
        } else {
          setError("Visitor inquiry details could not be loaded.");
        }
      })
      .catch(err => {
        if (!ignore) {
          setError(
            getApiErrorMessage(err, "Failed to load visitor inquiry details.")
          );
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [open, inquiryId, preview]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4 text-left">
          <div className="pr-8">
            <DialogTitle className="font-serif text-xl font-bold">
              {record ? `Visitor Inquiry #${record.id}` : "Visitor Inquiry"}
            </DialogTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {record
                ? `${record.visitor_name} · visit ${formatDate(record.visit_date) || record.visit_date || "—"}`
                : "Read-only view of the visitor inquiry"}
            </p>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading && !record ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mb-3 h-8 w-8 animate-spin" />
              Loading inquiry details…
            </div>
          ) : error && !record ? (
            <div className="border-destructive/30 bg-destructive/5 rounded-xl border px-4 py-6 text-center text-sm text-destructive">
              {error}
            </div>
          ) : record ? (
            <div className="space-y-6">
              <SectionPanel section={VISIT_DETAILS_SECTION}>
                <ViewField
                  label="Visit Date"
                  value={formatDate(record.visit_date) || record.visit_date}
                />
                <ViewField label="Visitor Name" value={record.visitor_name} />
                <ViewField
                  label="Date of Birth"
                  value={
                    record.date_of_birth
                      ? formatDate(record.date_of_birth) || record.date_of_birth
                      : undefined
                  }
                />
                <ViewField label="Age" value={record.age} />
                <ViewField
                  label="Married Status"
                  value={
                    record.married_status !== undefined
                      ? record.married_status
                        ? "Married"
                        : "Unmarried / Single"
                      : undefined
                  }
                />
                {record.married_status && record.marriage_anniversary_date ? (
                  <ViewField
                    label="Marriage Anniversary Date"
                    value={
                      formatDate(record.marriage_anniversary_date) ||
                      record.marriage_anniversary_date
                    }
                  />
                ) : null}
                <ViewField label="Occupation" value={record.occupation} />
                <ViewField label="Mobile Number" value={record.mobile_number} />
                <ViewField
                  label="Residence Phone"
                  value={record.residence_phone}
                />
                <ViewField label="Email" value={record.email} />
                <ViewField label="Address" value={record.address} full />
                <ViewField
                  label="Business Address"
                  value={record.business_address}
                  full
                />
              </SectionPanel>

              <SectionPanel section={REASON_SECTION}>
                <ViewField
                  label="Reason for Visit"
                  value={record.reason_for_visit}
                  full
                />
                <ViewField
                  label="Visited Before"
                  value={record.visited_before}
                />
              </SectionPanel>

              <SectionPanel section={REFERENCE_SECTION}>
                <ViewField
                  label="Reference Source"
                  value={referenceSourceLabel(record.reference_source)}
                />
                <ViewField label="Referrer Name" value={record.referrer_name} />
                <ViewField
                  label="Referrer Mobile"
                  value={record.referrer_mobile}
                />
              </SectionPanel>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
